import { createTheme, type PaletteColorOptions } from '@mui/material/styles';
import { grey } from '@mui/material/colors';
import Oreo from 'Oreo';

declare module '@mui/material/Button' {
    interface ButtonPropsColorOverrides {
        neutral: true;
    }
    interface ButtonPropsVariantOverrides {
        inputButton: true;
    }
}

declare module '@mui/material/styles' {
    interface Palette {
        neutral?: PaletteColorOptions;
    }
    interface PaletteOptions {
        neutral?: PaletteColorOptions;
    }
}

export const defaultTheme = createTheme({
    typography: {
        fontFamily: 'IBMPlexSans',
    },

    palette: {
        primary: {
            light: Oreo.COLOR_V3_BLUE_LIGHT,
            main: Oreo.COLOR_V3_BLUE,
            dark: Oreo.COLOR_V3_BLUE_DARK,
            contrastText: Oreo.COLOR_V3_WHITE,
        },

        secondary: {
            light: Oreo.COLOR_V3_CORAL,
            main: Oreo.COLOR_V3_CORAL,
            dark: Oreo.COLOR_V3_CORAL_DARK,
            contrastText: Oreo.COLOR_V3_WHITE,
        },

        neutral: {
            light: grey[200],
            main: grey[300],
            dark: grey[400],
            contrastText: Oreo.COLOR_V3_BLACK,
        },

        error: {
            main: Oreo.COLOR_V3_RED,
        },

        text: {
            primary: '#000000',
        },
    },

    breakpoints: {
        values: {
            xs: 0,
            sm: 768,
            md: 992,
            lg: 1200,
            xl: 1920,
        },
    },
});

/*
    The colour a `color` prop names, resolved against the palette. A Link takes any palette path, so
    'text.secondary' has to walk two levels; a Chip or an IconButton takes a palette group, whose
    colour is its `main`. Unresolvable names give nothing, and the caller decides what to do.
*/
function resolvePaletteColor(color: string | undefined) {
    if (!color || color === 'inherit' || color === 'default') return undefined;

    const resolved = color
        .split('.')
        .reduce<unknown>((node, key) => (node as Record<string, unknown> | undefined)?.[key], defaultTheme.palette);

    if (typeof resolved === 'string') return resolved;

    return (resolved as { main?: string } | undefined)?.main;
}

// The palette colour a control was given, or nothing when it has none or inherits.
function paletteMain(ownerState: { color?: string }) {
    if (!ownerState.color || ownerState.color === 'inherit' || ownerState.color === 'default') return undefined;

    return defaultTheme.palette[ownerState.color as 'primary']?.main;
}

// Where a Chip's label sits at rest: its palette colour's contrastText when filled, the ordinary
// text colour otherwise.
function restingColor(ownerState: { color?: string; variant?: string }) {
    if (!ownerState.color || ownerState.color === 'inherit' || ownerState.color === 'default') {
        return defaultTheme.palette.text.primary;
    }

    const paletteColor = defaultTheme.palette[ownerState.color as 'primary'];
    if (!paletteColor) return defaultTheme.palette.text.primary;

    return ownerState.variant === 'outlined' ? paletteColor.main : paletteColor.contrastText;
}

export const theme = createTheme(defaultTheme, {
    components: {
        MuiFormLabel: {
            styleOverrides: {
                asterisk: { color: Oreo.COLOR_V3_RED },
            },
        },

        MuiTooltip: {
            defaultProps: {
                // MUI defaults a tooltip to 'bottom'. The component library's Tooltip defaults to
                // top, so this matches that convention.
                //
                // The gap between a tooltip and its element is not set here. MUI's 14px is wide but
                // safe, and narrowing it globally brought the tip of an `arrow` tooltip down onto
                // its trigger. StreamEditor/streamEditorTheme.ts narrows it for that page alone.
                placement: 'top',
            },
        },

        /*
            A legacy global `a:hover { color: #35092c }` rule repaints the label of any MUI control
            that renders as an anchor: a Button with an href, a Chip that links, an IconButton that
            opens a page. Its specificity (0,1,1) beats the (0,1,0) class MUI sets the colour with,
            and MUI declares no hover colour of its own to defend it. `&&` doubles the class, which
            outranks the global rule. The four overrides below each restate their own resting
            colour.

            Each one is scoped to `a&&`, because the legacy rule only matches an anchor. Unscoped, a
            doubled class at (0,2,0) reaches every variant: it flattens the `color` prop of a Chip or
            an IconButton that renders as a button, and it outranks a call site's own `sx` hover
            block at (0,1,0).
        */
        MuiChip: {
            styleOverrides: {
                // ownerState, not a fixed colour: a Chip rests at its palette colour's contrastText
                // when it is filled and at text.primary otherwise, so one hardcoded value repaints
                // every coloured Chip that links.
                root: ({ ownerState }: { ownerState: { color?: string; variant?: string } }) => ({
                    'a&&:hover': { color: restingColor(ownerState) },
                }),
            },
        },

        MuiLink: {
            // A Link is not a ButtonBase, so the resets above miss it. MUI changes only the
            // underline on hover, never the colour. Its `color` takes a full palette path, so
            // 'text.secondary' has to resolve rather than fall through to inherit and hover to
            // whatever the surrounding text happens to be.
            styleOverrides: {
                root: ({ ownerState }: { ownerState: { color?: string } }) => ({
                    'a&&:hover': { color: resolvePaletteColor(ownerState.color) || 'inherit' },
                }),
            },
        },

        MuiIconButton: {
            styleOverrides: {
                // action.active is only where a default-coloured IconButton rests. A coloured one
                // rests at its palette main, so a fixed value flattened the colour prop.
                root: ({ ownerState }: { ownerState: { color?: string } }) => ({
                    'a&&:hover': { color: paletteMain(ownerState) || defaultTheme.palette.action.active },
                }),
            },
        },

        MuiButton: {
            styleOverrides: {
                root: ({ ownerState }: { ownerState: { color?: string; variant?: string } }) => {
                    const paletteColor =
                        ownerState.color && ownerState.color !== 'inherit'
                            ? defaultTheme.palette[ownerState.color as 'primary']
                            : undefined;

                    if (!paletteColor) return { 'a&&:hover': { color: 'inherit' } };

                    return {
                        'a&&:hover': {
                            color: ownerState.variant === 'contained' ? paletteColor.contrastText : paletteColor.main,
                        },
                    };
                },
            },

            variants: [
                {
                    props: { variant: 'inputButton' },
                    style: {
                        backgroundColor: grey['300'],
                        '&:hover': {
                            backgroundColor: grey['200'],
                        },
                        boxShadow: defaultTheme.shadows[1],
                        marginTop: defaultTheme.spacing(1),
                        marginBottom: defaultTheme.spacing(1),
                    },
                },
            ],
        },
    },
});

export default theme;
